import { useIntl } from 'react-intl';

import {
  TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL,
  TRADE_DIRECTION_UNSPECIFIED,
  TRADE_POINT_TYPE_RESIDUAL,
} from 'src/util/constants';
import { getTradePointTypeLabel } from 'src/util/i18n/helpers';

/**
 * Determines the trade direction for a given trade point ID within a trade rule.
 * @param {object} rule - The trade rule object containing buyer and seller information
 * @param {string} tradePointId - The trade point ID to check direction for
 * @returns {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL |
 * TRADE_DIRECTION_UNSPECIFIED} Trade direction constant
 */
export const getDirection = (rule, tradePointId) => {
  if (!rule || !tradePointId) {
    return TRADE_DIRECTION_UNSPECIFIED;
  }

  const buyerTradePointId = rule.buyer?.tradePoint?.uuid;
  const sellerTradePointId = rule.seller?.tradePoint?.uuid;

  if (buyerTradePointId === tradePointId) {
    return TRADE_DIRECTION_BUY;
  }

  if (sellerTradePointId === tradePointId) {
    return TRADE_DIRECTION_SELL;
  }

  return TRADE_DIRECTION_UNSPECIFIED;
};

/**
 * Custom hook that computes counter-party data for a trade rule.
 * Returns trader information, trade point details, and direction for the counter-party
 * in a trade rule based on the current trade point ID.
 * @param {object} rule - The trade rule object containing buyer and seller information
 * @param {string} tradePointId - The trade point ID to determine counter-party for
 * @returns {object|null} Object containing trader, the counterparty's recorded userId,
 * tradePoint, and direction, or null if
 * direction is unspecified
 */
export const useTradeRuleCounterParty = (rule, tradePointId) => {
  const intl = useIntl();

  const direction = getDirection(rule, tradePointId);

  if (direction === TRADE_DIRECTION_UNSPECIFIED) {
    return null;
  }

  const counterParty = direction === TRADE_DIRECTION_BUY ? rule?.seller : rule?.buyer;

  if (!counterParty) {
    return null;
  }

  let trader = null;
  if (counterParty.user) {
    trader = counterParty.user;
  } else if (counterParty.community) {
    trader = counterParty.community;
  } else if (counterParty.tradePoint?.type === TRADE_POINT_TYPE_RESIDUAL) {
    trader = counterParty.tradePoint;
  }

  let tradePoint = null;
  if (counterParty.tradePoint?.type) {
    const label = getTradePointTypeLabel(intl, counterParty.tradePoint.type);
    if (label) {
      tradePoint = { label, tradePoint: counterParty.tradePoint };
    }
  }

  return {
    trader, userId: counterParty.userId ?? null, tradePoint, direction,
  };
};
